export type DashboardPeriod = "today" | "week" | "month" | "year" | `year-${number}`;

export type DashboardDateRange = {
  startDate: string;
  endDate: string;
};

const pad = (n: number) => String(n).padStart(2, "0");

const fmt = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export function getDashboardDateRange(period: DashboardPeriod): DashboardDateRange {
  const now = new Date();
  const today = fmt(now);

  switch (period) {
    case "today":
      return { startDate: today, endDate: today };
    case "week": {
      const start = new Date(now);
      start.setDate(now.getDate() - now.getDay());
      return { startDate: fmt(start), endDate: today };
    }
    case "month": {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { startDate: fmt(start), endDate: today };
    }
    case "year": {
      const start = new Date(now.getFullYear(), 0, 1);
      return { startDate: fmt(start), endDate: today };
    }
    default: {
      const year = Number(period.replace("year-", ""));
      return { startDate: `${year}-01-01`, endDate: `${year}-12-31` };
    }
  }
}

export function getDashboardPeriodLabel(period: DashboardPeriod): string {
  if (period.startsWith("year-")) {
    return String(period.replace("year-", ""));
  }
  const labels: Record<Exclude<DashboardPeriod, `year-${number}`>, string> = {
    today: "Today",
    week: "This week",
    month: "This month",
    year: "This year",
  };
  return labels[period as keyof typeof labels];
}

export function getPastYearPeriods(
  userCreatedAt?: string | null,
): DashboardPeriod[] {
  const currentYear = new Date().getFullYear();
  const startYear = userCreatedAt
    ? new Date(userCreatedAt).getFullYear()
    : currentYear;

  const years: DashboardPeriod[] = [];
  for (let year = currentYear - 1; year >= startYear; year -= 1) {
    years.push(`year-${year}`);
  }
  return years;
}

export function getDashboardPeriodOptions(userCreatedAt?: string | null) {
  return [
    { label: "Today", value: "today" as const },
    { label: "This week", value: "week" as const },
    { label: "This month", value: "month" as const },
    { label: "This year", value: "year" as const },
    ...getPastYearPeriods(userCreatedAt).map((value) => ({
      label: getDashboardPeriodLabel(value),
      value,
    })),
  ];
}
